/**
 * Derived calculation hooks that connect the Zustand store to the calculation engine.
 * Use these in components instead of calling calculations.ts directly —
 * they automatically re-compute when store state changes.
 */

import { useMemo } from 'react';
import { useSimulatorStore } from '../stores/useSimulatorStore';
import {
  calculatePnL,
  calculatePortfolioPnL,
  calculateBreakevenAnalysis,
  generateSensitivityGrid,
  generatePortfolioSensitivityGrid,
  calculateWeightedSegmentDemand,
  rankAllFeatureBundles,
  calculateMarginalFeatureValues,
  interpolateDemandCurve,
} from '../lib/calculations';
import { S1_LEAD_LEVELS } from '../data/conjointData';

/**
 * P&L results for all three packages.
 */
export function usePackagePnLs() {
  const packages = useSimulatorStore(s => s.packages);
  const assumptions = useSimulatorStore(s => s.assumptions);

  return useMemo(
    () => packages.map(pkg => calculatePnL(pkg, assumptions)),
    [packages, assumptions]
  );
}

/**
 * P&L for a single package by index.
 */
export function usePackagePnL(index: number) {
  const pkg = useSimulatorStore(s => s.packages[index]);
  const assumptions = useSimulatorStore(s => s.assumptions);

  return useMemo(() => calculatePnL(pkg, assumptions), [pkg, assumptions]);
}

/**
 * Breakeven analysis for all price points.
 */
export function useBreakevenAnalysis() {
  const assumptions = useSimulatorStore(s => s.assumptions);
  return useMemo(() => calculateBreakevenAnalysis(assumptions), [assumptions]);
}

/**
 * Sensitivity grid: Price × S1 Leads.
 */
export function useSensitivityGrid() {
  const assumptions = useSimulatorStore(s => s.assumptions);
  return useMemo(
    () => generateSensitivityGrid(S1_LEAD_LEVELS, assumptions),
    [assumptions]
  );
}

/**
 * Segment demand at the currently selected price.
 */
export function useSegmentDemand() {
  const selectedPrice = useSimulatorStore(s => s.selectedPrice);
  return useMemo(() => calculateWeightedSegmentDemand(selectedPrice), [selectedPrice]);
}

/**
 * All 128 feature bundle combinations ranked by P&L.
 */
export function useBundleRanking() {
  const selectedPrice = useSimulatorStore(s => s.selectedPrice);
  const assumptions = useSimulatorStore(s => s.assumptions);

  return useMemo(
    () => rankAllFeatureBundles(selectedPrice, assumptions),
    [selectedPrice, assumptions]
  );
}

/**
 * Marginal feature value for the first package.
 */
export function useMarginalFeatureValues(packageIndex: number = 0) {
  const pkg = useSimulatorStore(s => s.packages[packageIndex]);
  const assumptions = useSimulatorStore(s => s.assumptions);

  return useMemo(
    () => calculateMarginalFeatureValues(pkg.enabledFeatures, pkg.price, assumptions),
    [pkg.enabledFeatures, pkg.price, assumptions]
  );
}

/**
 * Portfolio P&L: fixed costs applied once across all packages.
 * Returns per-package contribution margins and combined portfolio economics.
 */
export function usePortfolioPnL() {
  const packages = useSimulatorStore(s => s.packages);
  const assumptions = useSimulatorStore(s => s.assumptions);

  return useMemo(
    () => calculatePortfolioPnL(packages, assumptions),
    [packages, assumptions]
  );
}

/**
 * Portfolio sensitivity: S1 leads vs portfolio P&L.
 */
export function usePortfolioSensitivityGrid() {
  const packages = useSimulatorStore(s => s.packages);
  const assumptions = useSimulatorStore(s => s.assumptions);

  return useMemo(
    () => generatePortfolioSensitivityGrid(S1_LEAD_LEVELS, packages, assumptions),
    [packages, assumptions]
  );
}

/**
 * Interpolated demand curve for smooth charting.
 */
export function useDemandCurve(points: number = 50) {
  return useMemo(() => interpolateDemandCurve(points), [points]);
}
